import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { css, cx } from "@strust/ui/css";
import {
  avatar,
  badge,
  filterChip,
  idChip,
  meter,
  statusDot,
  tag,
  tooltip,
  type AvatarVariant,
  type BadgeVariant,
  type StatusDotVariant,
  type TagVariant,
} from "@strust/ui/recipes";
import {
  BanIcon,
  CheckIcon,
  CircleDashedIcon,
  CopyIcon,
  InfoIcon,
  LayersIcon,
  ServerIcon,
  ShieldCheckIcon,
  AlertTriangleIcon,
  XIcon,
} from "../icons/icons.tsx";
import { Skeleton } from "./feedback.tsx";
import { slots } from "../slots.ts";

export type BadgeTone = NonNullable<BadgeVariant["tone"]>;

const BADGE_ICONS: Partial<Record<BadgeTone, ReactNode>> = {
  allow: <CheckIcon />,
  deny: <XIcon />,
  advisory: <AlertTriangleIcon />,
  blocked: <BanIcon />,
  verified: <ShieldCheckIcon />,
  notEvaluated: <CircleDashedIcon />,
  info: <InfoIcon />,
};

export interface BadgeProps {
  /** allow, deny, advisory, blocked, verified, notEvaluated, neutral, info. */
  readonly tone?: BadgeTone;
  /** `lg` is the 34 px pill of the decision header. */
  readonly size?: "sm" | "md" | "lg";
  /** Overrides the tone's icon; `null` shows none. */
  readonly icon?: ReactNode;
  /** Text is always rendered: color is never the only signal. */
  readonly children: ReactNode;
  readonly className?: string;
}

/** Monospace uppercase pill with an icon (ALLOW, DENY, ADVISORY, BLOCKED, VERIFIED, NOT EVALUATED). */
export function Badge({ tone = "neutral", size = "md", icon, children, className }: BadgeProps) {
  const glyph = icon === undefined ? BADGE_ICONS[tone] : icon;
  return (
    <span className={cx(badge({ tone, size }), className)} data-tone={tone}>
      {glyph ? (
        <span aria-hidden="true" className={css({ display: "inline-flex" })}>
          {glyph}
        </span>
      ) : null}
      {children}
    </span>
  );
}

export type InstallationEnvironment = "staging" | "production";

export interface InstallationBadgeProps {
  /** The installation's environment; omit while `loading`. */
  readonly environment?: InstallationEnvironment;
  /** Localized text ("Staging", "Production"); while `loading`, the localized busy text. */
  readonly label: string;
  /** Neutral skeleton while the installation is being fetched. */
  readonly loading?: boolean;
  readonly className?: string;
}

const INSTALLATION_PRESENTATION = {
  staging: { tone: "info", icon: <LayersIcon /> },
  production: { tone: "advisory", icon: <ServerIcon /> },
} as const satisfies Record<InstallationEnvironment, { tone: BadgeTone; icon: ReactNode }>;

/**
 * Read-only indicator of the environment this installation serves (staging: accent, production:
 * warning). The text always says the environment (never color alone) and there is no interactive
 * role: the operator cannot switch it.
 */
export function InstallationBadge({
  environment,
  label,
  loading = false,
  className,
}: InstallationBadgeProps) {
  if (loading || environment === undefined) {
    return (
      <span
        role="status"
        aria-busy="true"
        data-installation="loading"
        className={cx(css({ display: "inline-flex", alignItems: "center" }), className)}
      >
        <span className={css({ srOnly: true })}>{label}</span>
        <Skeleton width="6.5rem" height="1.5rem" />
      </span>
    );
  }
  const { tone, icon } = INSTALLATION_PRESENTATION[environment];
  return (
    <span className={cx(badge({ tone }), className)} data-installation={environment}>
      <span aria-hidden="true" className={css({ display: "inline-flex" })}>
        {icon}
      </span>
      {label}
    </span>
  );
}

export type DecisionVariant = "allow" | "deny";

/**
 * Maps a Decision received from the API to its visual variant. Anything other than `allow` (an
 * unknown or future value such as `review`) renders as denied: the safe default (spec D16).
 */
export function toDecisionVariant(decision: string): DecisionVariant {
  return decision === "allow" ? "allow" : "deny";
}

export interface DecisionBadgeProps {
  /** Raw decision from the API; unknown values render as denied. */
  readonly decision: string;
  /** Localized text for the resolved variant (`decision.allow` / `decision.deny`). */
  readonly label: string;
  readonly size?: "sm" | "md" | "lg";
}

export function DecisionBadge({ decision, label, size = "md" }: DecisionBadgeProps) {
  const variant = toDecisionVariant(decision);
  return (
    <span data-decision={variant} className={css({ display: "inline-flex" })}>
      <Badge tone={variant} size={size}>
        {label}
      </Badge>
    </span>
  );
}

export interface TagProps {
  readonly children: ReactNode;
  readonly icon?: ReactNode;
  readonly mono?: boolean;
  readonly tone?: NonNullable<TagVariant["tone"]>;
  readonly className?: string;
}

/** Small label for reasons, regions, environments and other attributes. */
export function Tag({ children, icon, mono = false, tone = "neutral", className }: TagProps) {
  return (
    <span className={cx(tag({ mono, tone }), className)}>
      {icon ? (
        <span aria-hidden="true" className={css({ display: "inline-flex" })}>
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}

type CopyState = "idle" | "copied" | "failed";

/** Clipboard write with a timed, announced confirmation. */
function useCopy(value: string, resetAfterMs: number) {
  const [state, setState] = useState<CopyState>("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
    } catch {
      setState("failed");
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setState("idle");
    }, resetAfterMs);
  };
  return { state, copy };
}

export interface CopyButtonProps {
  /** The text written to the clipboard. */
  readonly value: string;
  readonly label: string;
  readonly copiedLabel: string;
  readonly failedLabel: string;
  /** How long the confirmation stays, in ms. */
  readonly resetAfterMs?: number;
  /** Shows the label as text next to the icon (default: icon only, label as accessible name). */
  readonly showLabel?: boolean;
  /**
   * Revealed on row hover and focus (table cells and lists, see `IdChip` `bare`); it stays
   * focusable and is always visible where there is no hover.
   */
  readonly reveal?: boolean;
}

/** Copy action with an announced result (`role="status"`). */
export function CopyButton({
  value,
  label,
  copiedLabel,
  failedLabel,
  resetAfterMs = 2000,
  showLabel = false,
  reveal = false,
}: CopyButtonProps) {
  const { state, copy } = useCopy(value, resetAfterMs);
  const s = idChip({ bare: reveal });
  return (
    <>
      <button
        type="button"
        className={
          showLabel
            ? cx(
                s.copy,
                css({ width: "auto", px: "2", gap: "1.5", display: "inline-flex", fontSize: "sm" }),
              )
            : s.copy
        }
        aria-label={showLabel ? undefined : label}
        title={label}
        onClick={() => {
          void copy();
        }}
      >
        {state === "copied" ? <CheckIcon aria-hidden="true" /> : <CopyIcon aria-hidden="true" />}
        {showLabel ? label : null}
      </button>
      <span role="status" className={s.status}>
        {state === "copied" ? copiedLabel : state === "failed" ? failedLabel : ""}
      </span>
    </>
  );
}

/**
 * Middle truncation of an identifier: the first `head` and the last `tail` characters around an
 * ellipsis (`0192f3aa…19d4`), computed here and not by CSS so the visible text is stable. An id
 * that would not get shorter is returned whole.
 */
export function shortenId(id: string, head = 8, tail = 4): string {
  const characters = Array.from(id);
  if (characters.length <= head + tail + 1) return id;
  return `${characters.slice(0, head).join("")}\u2026${characters.slice(-tail).join("")}`;
}

export interface TruncatedIdProps {
  /** The full identifier. */
  readonly id: string;
  readonly head?: number;
  readonly tail?: number;
}

/**
 * An identifier shortened in the middle for sighted users; assistive technology reads the full
 * value (the shortened text is hidden from it) and the full value is the tooltip (`title`).
 */
export function TruncatedId({ id, head = 8, tail = 4 }: TruncatedIdProps) {
  const short = shortenId(id, head, tail);
  if (short === id) return <>{id}</>;
  return (
    <span title={id}>
      <span aria-hidden="true">{short}</span>
      <span className={css({ srOnly: true })}>{id}</span>
    </span>
  );
}

export interface IdChipProps extends Omit<CopyButtonProps, "value" | "showLabel" | "reveal"> {
  /** The identifier (verification_id, player_ref, device_ref, IP, client_id). */
  readonly id: string;
  /** Text shown instead of the full id (e.g. a shortened form); the full id is copied. */
  readonly display?: string;
  /** Wraps long ids instead of truncating them. */
  readonly wrap?: boolean;
  /** No frame, for table cells and lists; the copy button shows on row hover and focus. */
  readonly bare?: boolean;
  /** Middle truncation (8 + 4 characters); defaults to on for `bare`, off for the framed chip. */
  readonly truncate?: boolean;
  /**
   * Renders the value as a link (a router link): receives the layout class and the visible
   * content; the caller adds the link's own styles. The full id stays its accessible name.
   */
  readonly renderLink?: (props: {
    readonly className: string;
    readonly children: ReactNode;
  }) => ReactNode;
  readonly className?: string;
}

/**
 * Identifier in monospace accent with a copy button. The framed chip suits headers and dialogs;
 * `bare` suits table cells and lists (one line, middle truncation, copy on hover or focus).
 */
export function IdChip({
  id,
  display,
  wrap = false,
  bare = false,
  truncate = bare,
  renderLink,
  className,
  ...labels
}: IdChipProps) {
  const s = slots(idChip({ wrap, bare }));
  const content = display ?? (truncate && !wrap ? <TruncatedId id={id} /> : id);
  return (
    <span className={cx(s.root, className)} data-id-chip="">
      {renderLink ? (
        renderLink({ className: s.link, children: content })
      ) : (
        <code className={s.value} title={display !== undefined ? id : undefined}>
          {content}
        </code>
      )}
      <CopyButton value={id} reveal={bare} {...labels} />
    </span>
  );
}

export interface FilterChipProps {
  /** The filter's name (`Decision`), or the whole text (`Decision: deny`) when there is no `value`. */
  readonly label: string;
  /** The applied value, shown after the label as `label: value`. */
  readonly value?: string;
  /** Decorative icon before the label. */
  readonly icon?: ReactNode;
  /** Accessible name of the remove button, e.g. "Remove filter Decision: deny". */
  readonly removeLabel: string;
  readonly onRemove: () => void;
}

/** An applied filter (`icon · label: value`) with a remove button. */
export function FilterChip({ label, value, icon, removeLabel, onRemove }: FilterChipProps) {
  const s = filterChip();
  return (
    <span className={s.root}>
      {icon ? (
        <span aria-hidden="true" className={s.icon}>
          {icon}
        </span>
      ) : null}
      <span className={s.label}>{value === undefined ? label : `${label}:`}</span>
      {value === undefined ? null : (
        <>
          {" "}
          <span className={s.value}>{value}</span>
        </>
      )}
      <button
        type="button"
        className={s.remove}
        aria-label={removeLabel}
        title={removeLabel}
        onClick={onRemove}
      >
        <XIcon aria-hidden="true" />
      </button>
    </span>
  );
}

export interface StatusDotProps {
  readonly tone?: NonNullable<StatusDotVariant["tone"]>;
  /** Text is always rendered next to the dot. */
  readonly children: ReactNode;
  readonly className?: string;
}

/** Glowing status dot with its text (Active, Locked, Deactivated, Live). */
export function StatusDot({ tone = "ok", children, className }: StatusDotProps) {
  const s = statusDot({ tone });
  return (
    <span className={cx(s.root, className)} data-tone={tone}>
      <span aria-hidden="true" className={s.dot} />
      <span className={s.label}>{children}</span>
    </span>
  );
}

export interface AvatarProps {
  /** Initials (decorative); the accessible name comes from the surrounding control or `label`. */
  readonly initials?: string;
  readonly icon?: ReactNode;
  readonly label?: string;
  readonly size?: NonNullable<AvatarVariant["size"]>;
  readonly tone?: NonNullable<AvatarVariant["tone"]>;
}

export function Avatar({ initials, icon, label, size = "md", tone = "steel" }: AvatarProps) {
  return (
    <span
      className={avatar({ size, tone })}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    >
      {icon ?? initials}
    </span>
  );
}

export interface MeterProps {
  readonly label: string;
  /** 0..1 */
  readonly value: number;
  /** Already formatted (Intl), e.g. "25%". */
  readonly valueText: string;
  readonly className?: string;
}

function Meter({
  kind,
  label,
  value,
  valueText,
  className,
}: MeterProps & { readonly kind: "risk" | "progress" }) {
  const s = meter({ kind });
  const labelId = useId();
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className={cx(s.root, className)}>
      <div className={s.head}>
        <span id={labelId} className={s.label}>
          {label}
        </span>
        <span className={s.value} aria-hidden="true">
          {valueText}
        </span>
      </div>
      <div
        role={kind === "progress" ? "progressbar" : "meter"}
        aria-labelledby={labelId}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        aria-valuetext={valueText}
        className={s.track}
      >
        <span className={s.bar} style={{ width: `${String(pct)}%` }} />
      </div>
    </div>
  );
}

/** Risk meter (e.g. failure rate): warn → deny gradient bar. */
export function RiskMeter(props: MeterProps) {
  return <Meter kind="risk" {...props} />;
}

/** Progress bar (e.g. export generation) with a glowing accent bar. */
export function Progress(props: MeterProps) {
  return <Meter kind="progress" {...props} />;
}

export interface TooltipProps {
  /** Accessible name of the trigger, e.g. "About the primary source". */
  readonly label: string;
  readonly title?: string;
  readonly children: ReactNode;
  readonly icon?: ReactNode;
}

/**
 * Inverted tooltip on a small info trigger: shown on hover and keyboard focus, dismissed with
 * Escape (WCAG 1.4.13); the content describes the trigger.
 */
export function Tooltip({ label, title, children, icon }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const s = tooltip();
  return (
    <span
      className={s.root}
      onMouseEnter={() => {
        setOpen(true);
      }}
      onMouseLeave={() => {
        setOpen(false);
      }}
    >
      <button
        type="button"
        className={s.trigger}
        aria-label={label}
        aria-describedby={open ? id : undefined}
        onFocus={() => {
          setOpen(true);
        }}
        onBlur={() => {
          setOpen(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      >
        {icon ?? <InfoIcon aria-hidden="true" />}
      </button>
      {open ? (
        <span role="tooltip" id={id} className={s.content}>
          {title ? <span className={s.title}>{title}</span> : null}
          <span className={s.body}>{children}</span>
        </span>
      ) : null}
    </span>
  );
}
